import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { match } from 'ts-pattern'
import { UIButton } from 'src/components/ui/Button'
import {
  UIDialog,
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
} from 'src/components/ui/Modal'
import { useMyMembership } from 'src/services/apollo/membership'
import { useMembershipActions } from 'src/services/helpers/membership'
import { usePaymentMethodAutoRenewable } from '../hooks/usePaymentMethodAutoRenewable'
import { getSubscriptionState } from '../utils/subscriptionStateUtils'

export const showMembershipAutoRenewDialog = () => {
  return UIDialog.create({
    dismissable: false,
    children: ({ close }) => <MembershipAutoRenewDialog onClose={close} />,
  })
}

const MembershipAutoRenewDialog = ({ onClose }: { onClose?: () => void }) => {
  const { t } = useTranslation(['membership'])

  const { data: membership } = useMyMembership()
  const subscriptionState = useMemo(
    () =>
      membership?.subscription
        ? getSubscriptionState(membership.subscription)
        : undefined,
    [membership],
  )
  const isAutoRenewable = usePaymentMethodAutoRenewable()

  const dialogStatus: {
    text: string
    image: 'happy' | 'sad'
    action: 'enable' | 'disable' | 'new-membership' | 'none'
  } = useMemo(() => {
    // If the state is NOT active, show a message indicating no subscription
    if (subscriptionState?.state !== 'active') {
      return {
        text: t(
          'membership:membership.auto-renewal.manage.dialog.content_no_subscription',
        ),
        image: 'sad',
        action: 'new-membership' as const,
      }
    }

    const current = subscriptionState.current
    const provider = current.paymentProvider
    const autoRenewable = current.autoRenewable

    return match(autoRenewable)
      .with('enabled', () => {
        return {
          text: t(
            'membership:membership.auto-renewal.manage.dialog.content_enabled',
          ),
          image: 'happy' as const,
          action: 'disable' as const,
        }
      })
      .with('unspecified', () => {
        // Determine the action based on if the provider supports auto-renewal
        if (isAutoRenewable(provider)) {
          return {
            text: t(
              'membership:membership.auto-renewal.manage.dialog.content_supported',
            ),
            image: 'sad' as const,
            action: 'enable' as const,
          }
        }

        return {
          text: t(
            'membership:membership.auto-renewal.manage.dialog.content_unsupported',
          ),
          image: 'sad' as const,
          action: 'none' as const,
        }
      })
      .with('mobile-app-store', () => {
        // For mobile app stores, we cannot manage auto-renewal directly
        // so we show a message indicating to manage it in the app store
        return {
          text: t('membership:membership.auto-renewal.manage.dialog.content', {
            context: provider.toString(),
          }),
          image: 'sad' as const,
          action: 'none' as const,
        }
      })
      .exhaustive()
  }, [isAutoRenewable, subscriptionState, t])

  // Actions
  const navigate = useNavigate()
  const { unsubscribe, resume } = useMembershipActions()

  const onActionButtonPress = async () => {
    switch (dialogStatus.action) {
      case 'none': {
        return
      }
      case 'new-membership': {
        navigate('/membership/plans')
        onClose?.()
        return
      }
      case 'enable': {
        await resume()
        onClose?.()
        return
      }
      case 'disable': {
        await unsubscribe()
        onClose?.()
        return
      }
    }
  }

  return (
    <>
      <UIDialogHeader
        heading={t('membership:membership.auto-renewal.manage.dialog.title')}
      />

      <UIDialogContent>
        <div className="flex flex-col gap-4 mx-auto">
          <img
            src={t('membership:membership.auto-renewal.manage.dialog.image', {
              context: dialogStatus.image,
            })}
            alt=""
            className="object-scale-down object-center w-full"
          />
          <p className="text-indent text-base">{dialogStatus.text}</p>
          {dialogStatus.action !== 'none' && (
            <UIButton
              variant={
                dialogStatus.action === 'disable' ? 'outlined' : 'filled'
              }
              color="theme"
              size="large"
              className="w-full"
              onPress={onActionButtonPress}
            >
              {t('membership:membership.auto-renewal.manage.dialog.action', {
                context: dialogStatus.action,
              })}
            </UIButton>
          )}
        </div>
      </UIDialogContent>

      <UIDialogFooter>
        <UIButton variant="outlined" onPress={onClose}>
          {t('common:close')}
        </UIButton>
      </UIDialogFooter>
    </>
  )
}
